import React from "react";
import { ITrendAnalysisColorWithDetails } from "../../interface/TrendAnalysisColorInterface";
import { formatGenderLabel } from "../../utils/formatGenderLabel";
import ColorSwatchBox from "./ColorSwatchBox";
import DetailRow from "./DetailRow";

type ColorComparisonCardProps = {
  item: ITrendAnalysisColorWithDetails;
  index: number;
};

const ColorComparisonCard: React.FC<ColorComparisonCardProps> = ({
  item,
  index,
}) => {
  return (
    <article className="bg-white rounded-2xl border border-light shadow-sm p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5 pb-4 border-b border-light">
        <div>
          <p className="text-xs uppercase tracking-wide text-tertiary">
            Row {index + 1}
          </p>
          <h3 className="text-lg font-semibold text-primary">
            {item.apparel?.name || "Unknown apparel"}
          </h3>
        </div>
        <span className="text-sm px-3 py-1 rounded-full bg-brandPrimarySubtle text-primary">
          Dominance: {item.dominance_power ?? "—"}
        </span>
      </div>

      <div className="mb-5 rounded-xl bg-lightGray p-4">
        <p className="text-xs uppercase tracking-wide text-tertiary mb-3">
          Apparel
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <DetailRow
            label="ID"
            value={item.apparel?.id ?? item.Apparel_ref_id}
          />
          <DetailRow label="Name" value={item.apparel?.name} />
          <DetailRow label="Type" value={item.apparel?.type} />
          <DetailRow
            label="Gender"
            value={formatGenderLabel(item.apparel?.gender)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <section className="rounded-xl border border-light p-4">
          <h4 className="text-sm font-semibold text-primary mb-4">
            Trend Analysis Color
          </h4>

          <div className="mb-4">
            <ColorSwatchBox label="AI Color Hex" hex={item.AI_Color_HexCode} />
          </div>

          <div className="rounded-lg bg-lightGray p-3">
            <DetailRow label="ID" value={item.id} />
            <DetailRow label="Meta Post ID" value={item.meta_postId} />
            <DetailRow label="Apparel Ref ID" value={item.Apparel_ref_id} />
            <DetailRow
              label="Nearest Pantone Name"
              value={item.Nearest_Pantone_Name}
            />
            <DetailRow label="Dominance Power" value={item.dominance_power} />
            <DetailRow label="Created At" value={item.createdAt} />
            <DetailRow label="Updated At" value={item.updatedAt} />
          </div>
        </section>

        <section className="rounded-xl border border-light p-4">
          <h4 className="text-sm font-semibold text-primary mb-4">
            Pantones New
          </h4>

          <div className="mb-4">
            <ColorSwatchBox
              label="Pantone Hex"
              hex={item.pantone?.hex_code || ""}
            />
          </div>

          <div className="rounded-lg bg-lightGray p-3">
            {item.pantone ? (
              <>
                <DetailRow label="ID" value={item.pantone.id} />
                <DetailRow
                  label="Color Family"
                  value={item.pantone.color_family}
                />
                <DetailRow
                  label="Pantone Name"
                  value={item.pantone.pantone_name}
                />
                <DetailRow
                  label="Pantone Code"
                  value={item.pantone.pantone_code}
                />
                <DetailRow label="Hex Code" value={item.pantone.hex_code} />
                <DetailRow
                  label="RGB"
                  value={
                    item.pantone.rgb.length
                      ? `[${item.pantone.rgb.join(", ")}]`
                      : null
                  }
                />
                <DetailRow
                  label="Is Original"
                  value={item.pantone.is_original}
                />
              </>
            ) : (
              <p className="text-sm text-tertiary py-2">
                No matching pantone found in pantones_new for this hex code.
              </p>
            )}
          </div>
        </section>
      </div>
    </article>
  );
};

export default ColorComparisonCard;
